/**
 * FamiliarisationSidebar - Sidebar content for vessel requirements and quick actions
 */

import React, { useState } from 'react';
import { Shield, FileText, BookOpen, Settings, FileDown, Zap, Ship } from 'lucide-react';
import { exportFamiliarisationSummaryPDF } from '../../../../utils/pdf/familiarisationExport';
import FamiliarisationGuideModal from './FamiliarisationGuideModal';
import { useLegacyStorage } from '../../../../hooks/useLegacyStorage';

const FamiliarisationSidebar = ({ currentVessel, vesselFamiliarisationPreview, onSettingsClick }) => {
  const [showGuide, setShowGuide] = useState(false);
  const storage = useLegacyStorage();
  
  return (
    <div className="space-y-6">
      {/* Current Vessel Requirements */}
      {currentVessel && vesselFamiliarisationPreview && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Ship className="h-5 w-5 text-white" />
              Vessel Requirements
            </h3>
            <p className="mt-0.5 text-sm text-slate-300">{currentVessel.name}</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">Familiarisation Sections:</p>
                <div className="space-y-2">
                  {vesselFamiliarisationPreview.map((section, index) => (
                    <div key={section.section} className="flex items-center text-sm">
                      <div className="flex-shrink-0 w-6 h-6 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>
                      <span className="ml-3 text-gray-700">{section.title}</span>
                      <span className="ml-auto text-xs text-gray-500">
                        {section.items?.length || 0} items
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="h-4 w-4 mr-2" />
                  <span>WBC3 Appendix 8, Section 7.1 & 7.2</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>UK Workboat Code Edition 3</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-white" />
            Quick Actions
          </h3>
        </div>
        <div className="p-6 space-y-3">
          <button 
            onClick={onSettingsClick}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Customise Checklists
          </button>
          <button 
            onClick={() => {
              const records = storage.get('familiarisation_records', []);
              // FT-181: Use correct storage key 'fleet_crew' (not deprecated 'crewMembers')
              const crew = storage.get('fleet_crew', []);
              
              if (records.length === 0) {
                alert('No familiarisation records to export.');
                return;
              }
              
              const exportChoice = window.confirm(
                'Export Options:\n\n' +
                'OK - Export Summary PDF (all records)\n' +
                'Cancel - View individual records to export\n\n' +
                'Note: Individual records can be exported from the crew familiarisation view.'
              );
              
              if (exportChoice) {
                exportFamiliarisationSummaryPDF(records, crew, currentVessel);
              }
            }}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export Records
          </button>
          <button 
            onClick={() => setShowGuide(true)}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Familiarisation Guide
          </button>
        </div>
      </div>
      
      {/* Guide Modal */}
      <FamiliarisationGuideModal 
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
};

export default FamiliarisationSidebar;